import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';

// ─── Types ────────────────────────────────────────────────────────────────────

type AnswerKind = 'header' | 'text' | 'multichoice';

interface QuestionEntry {
  localId: string;
  text: string;
  kind: AnswerKind;
  choices: string; // comma-separated, only used for multichoice
}

interface EditableForm {
  form_id: number;
  name: string;
  entries: QuestionEntry[];
}

interface CreateFormProps {
  onClose: () => void;
  editingForm?: EditableForm | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function findAnswerTypeId(
  answerTypes: Record<string, string[]>,
  options: string[],
): number | null {
  for (const [id, opts] of Object.entries(answerTypes)) {
    if (JSON.stringify(opts) === JSON.stringify(options)) return Number(id);
  }
  return null;
}

function nextNumericKey(map: Record<string, unknown>): number {
  const keys = Object.keys(map).map(Number).filter(n => !isNaN(n));
  return keys.length > 0 ? Math.max(...keys) + 1 : 1;
}

function kindToOptions(kind: AnswerKind, choices: string): string[] {
  if (kind === 'header')      return [];
  if (kind === 'text')        return [''];
  return choices.split(',').map(s => s.trim()).filter(Boolean);
}

const emptyEntry = (): QuestionEntry => ({
  localId: crypto.randomUUID(),
  text: '',
  kind: 'text',
  choices: '',
});

// ─── Component ────────────────────────────────────────────────────────────────

const CreateForm: React.FC<CreateFormProps> = ({ onClose, editingForm }) => {
  const [formName, setFormName] = useState('');
  const [entries, setEntries] = useState<QuestionEntry[]>([emptyEntry()]);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!editingForm) return;
    setFormName(editingForm.name);
    setEntries(editingForm.entries.length > 0 ? editingForm.entries : [emptyEntry()]);
  }, [editingForm]);

  const addEntry = () => setEntries(prev => [...prev, emptyEntry()]);
  const removeEntry = (id: string) => setEntries(prev => prev.filter(e => e.localId !== id));
  const updateEntry = <K extends keyof QuestionEntry>(id: string, field: K, value: QuestionEntry[K]) =>
    setEntries(prev => prev.map(e => e.localId === id ? { ...e, [field]: value } : e));

  const isValid = () =>
    formName.trim() !== '' &&
    entries.length > 0 &&
    entries.every(e => {
      if (!e.text.trim()) return false;
      if (e.kind === 'multichoice' && !e.choices.trim()) return false;
      return true;
    });

  const handleSubmit = async () => {
    if (!isValid()) { setStatus('Fill in all fields.'); return; }
    setLoading(true);
    setStatus(null);

    try {
      const [questionsSnap, answerTypesSnap, structuresSnap] = await Promise.all([
        getDoc(doc(db, 'forms', 'questions')),
        getDoc(doc(db, 'forms', 'answer_types')),
        getDoc(doc(db, 'forms', 'structures')),
      ]);

      const questionsData: Record<string, string>   = (questionsSnap.data()    as any) || {};
      const answerTypesData: Record<string, string[]> = (answerTypesSnap.data() as any) || {};
      const structuresData: Record<string, any>     = (structuresSnap.data()   as any) || {};

      let nextQId = nextNumericKey(questionsData);
      let nextAId = nextNumericKey(answerTypesData);

      const formId = editingForm
        ? editingForm.form_id
        : nextNumericKey(structuresData);

      const newQuestions:    Record<string, string>   = {};
      const newAnswerTypes:  Record<string, string[]> = {};
      const questionIds:     number[]                 = [];
      const answerTypeIds:   number[]                 = [];

      for (const entry of entries) {
        // Question: always allocate a new ID
        const qId = nextQId++;
        newQuestions[String(qId)] = entry.text.trim();
        questionIds.push(qId);

        // Answer type: reuse existing or create
        const options = kindToOptions(entry.kind, entry.choices);
        const existing = findAnswerTypeId(answerTypesData, options);
        if (existing !== null) {
          answerTypeIds.push(existing);
        } else {
          const aId = nextAId++;
          newAnswerTypes[String(aId)] = options;
          answerTypesData[String(aId)] = options; // keep local state consistent for dedup
          answerTypeIds.push(aId);
        }
      }

      await Promise.all([
        updateDoc(doc(db, 'forms', 'questions'), newQuestions),
        Object.keys(newAnswerTypes).length > 0
          ? updateDoc(doc(db, 'forms', 'answer_types'), newAnswerTypes)
          : Promise.resolve(),
        setDoc(
          doc(db, 'forms', 'structures'),
          { [String(formId)]: { name: formName.trim(), questions: questionIds, answerTypes: answerTypeIds } },
          { merge: true },
        ),
      ]);

      setStatus(editingForm ? 'Form updated.' : 'Form created.');
      setTimeout(onClose, 700);
    } catch (e) {
      console.error(e);
      setStatus('Error saving form.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white h-5/6 w-11/12 lg:w-3/4 xl:w-1/2 p-8 rounded-3xl shadow-2xl relative overflow-auto">
        <button className="btn btn-sm btn-circle absolute right-2 top-2" onClick={onClose}>✕</button>
        <h2 className="text-3xl font-semibold text-gray-800 mb-6">
          {editingForm ? 'Edit Form' : 'Create Form'}
        </h2>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
          <input
            type="text"
            value={formName}
            onChange={e => setFormName(e.target.value)}
            placeholder="e.g. Intake Assessment"
            className="input input-bordered w-full"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-700 mb-3">Questions</h3>
        <div className="space-y-3 mb-4">
          {entries.map((e, i) => (
            <div key={e.localId} className="bg-gray-50 p-4 rounded-xl flex gap-3 items-start">
              <span className="text-sm font-medium text-gray-400 mt-2 w-5 shrink-0">{i + 1}.</span>
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={e.text}
                  onChange={ev => updateEntry(e.localId, 'text', ev.target.value)}
                  placeholder={e.kind === 'header' ? 'Section heading text' : 'Question text'}
                  className="input input-bordered input-sm w-full"
                />
                <select
                  value={e.kind}
                  onChange={ev => updateEntry(e.localId, 'kind', ev.target.value as AnswerKind)}
                  className="select select-bordered select-sm w-full"
                >
                  <option value="text">Text input</option>
                  <option value="multichoice">Multiple choice</option>
                  <option value="header">Section header (display only)</option>
                </select>
                {e.kind === 'multichoice' && (
                  <input
                    type="text"
                    value={e.choices}
                    onChange={ev => updateEntry(e.localId, 'choices', ev.target.value)}
                    placeholder="Option A, Option B, Option C"
                    className="input input-bordered input-sm w-full"
                  />
                )}
              </div>
              <button
                className="btn btn-ghost btn-sm text-red-400 mt-1"
                onClick={() => removeEntry(e.localId)}
                disabled={entries.length === 1}
              >✕</button>
            </div>
          ))}
        </div>

        <button className="btn btn-outline btn-sm mb-6" onClick={addEntry}>+ Add Question</button>

        {status && (
          <p className={`text-sm mb-4 ${status.startsWith('Error') || status.startsWith('Fill') ? 'text-red-500' : 'text-green-600'}`}>
            {status}
          </p>
        )}

        <button
          className="btn btn-primary w-full"
          onClick={handleSubmit}
          disabled={loading || !isValid()}
        >
          {loading ? 'Saving…' : editingForm ? 'Update Form' : 'Create Form'}
        </button>
      </div>
    </div>
  );
};

export default CreateForm;
export type { EditableForm, QuestionEntry, AnswerKind };
